/**
 * CommandEventsTable — §1 "surveillance operations room" events list renderer.
 *
 * Dense monospace table: 56px thumb, mono timestamp, camera, detection chips,
 * confidence bar (h-1 w-16 bg-muted + bg-primary fill at max confidence),
 * row hover = amber left edge via box-shadow inset.
 * Selection checkbox shown as first cell when selectionMode is active.
 * Actions menu (open / download / delete) reuses the existing 3-dots dropdown items.
 *
 * Props: plain data + callback refs only — no query hooks, no new fetches.
 */

import { useMemo, useState } from 'react'
import {
  Download,
  Trash2,
  MoreVertical,
  ExternalLink,
  Play,
  Image,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DetectionChip } from '@/components/ui/detection-chip'
import { getEventDownloads, downloadFile } from '@/lib/eventDownload'
import { cn, formatRelativeTime } from '@/lib/utils'
import type { Event, VisualDetection } from '@/types'

// ── helpers ──────────────────────────────────────────────────────────────────

/** Maximum visual detection confidence across all frames for the given event. */
function maxConfidence(event: Event): number {
  const detections = (event.metadata?.detections as VisualDetection[]) ?? []
  if (!detections.length) return event.confidence_score
  return detections.reduce((m, d) => Math.max(m, d.confidence), 0)
}

// ── sub-components ────────────────────────────────────────────────────────────

function Thumb({ event }: { event: Event }) {
  const [imgError, setImgError] = useState(false)
  const mediaType = event.media_details?.media_type ?? 'image'
  return (
    <div className="relative h-[56px] w-[56px] flex-shrink-0 overflow-hidden rounded bg-muted">
      {event.thumbnail_url && !imgError ? (
        <img
          src={event.thumbnail_url}
          alt={event.event_type}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          {mediaType === 'video' ? (
            <Play className="h-4 w-4 text-muted-foreground/50" />
          ) : (
            <Image className="h-4 w-4 text-muted-foreground/50" />
          )}
        </div>
      )}
    </div>
  )
}

function ConfBar({ pct }: { pct: number }) {
  return (
    <div className="h-1 w-16 rounded bg-muted overflow-hidden flex-shrink-0">
      <div
        className="h-full bg-primary"
        style={{ width: `${Math.round(Math.min(1, pct) * 100)}%` }}
      />
    </div>
  )
}

function SelectCell({ selected }: { selected: boolean }) {
  return (
    <div
      className={cn(
        'h-4 w-4 rounded border flex items-center justify-center flex-shrink-0',
        selected ? 'bg-primary border-primary' : 'border-muted-foreground/40',
      )}
    >
      {selected && (
        <svg viewBox="0 0 8 8" className="h-2.5 w-2.5 fill-primary-foreground">
          <path d="M1 4l2 2 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </div>
  )
}

// ── main component ────────────────────────────────────────────────────────────

export interface CommandEventsTableHandlers {
  onEventClick: (index: number) => void
  onToggleSelect: (id: number) => void
  onDeleteRequest: (event: Event) => void
}

export interface CommandEventsTableProps extends CommandEventsTableHandlers {
  events: Event[]
  selectionMode: boolean
  selectedIds: Set<number>
}

export function CommandEventsTable({
  events,
  selectionMode,
  selectedIds,
  onEventClick,
  onToggleSelect,
  onDeleteRequest,
}: CommandEventsTableProps) {
  const { t } = useTranslation('events')

  return (
    <div className="overflow-x-auto rounded border border-border">
      <table className="w-full font-mono text-[12px] border-collapse">
        {/* Header — sticky removed; table grows page for correct sentinel behaviour */}
        <thead className="bg-card">
          <tr>
            {selectionMode && <th className="w-8 px-3 py-1.5" />}
            <th className="w-[72px] px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              {t('table.media', { defaultValue: 'Media' })}
            </th>
            <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium whitespace-nowrap">
              {t('table.timestamp', { defaultValue: 'Timestamp' })}
            </th>
            <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              {t('table.camera', { defaultValue: 'Camera' })}
            </th>
            <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              {t('table.detections', { defaultValue: 'Detections' })}
            </th>
            <th className="w-20 px-3 py-1.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              {t('table.conf', { defaultValue: 'Conf' })}
            </th>
            <th className="w-8 px-2 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {events.map((event, index) => (
            <CommandRow
              key={event.id}
              event={event}
              index={index}
              selectionMode={selectionMode}
              selected={selectedIds.has(event.id)}
              onEventClick={onEventClick}
              onToggleSelect={onToggleSelect}
              onDeleteRequest={onDeleteRequest}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── row sub-component (calls useTranslation directly) ─────────────────────────

interface CommandRowProps {
  event: Event
  index: number
  selectionMode: boolean
  selected: boolean
  onEventClick: (i: number) => void
  onToggleSelect: (id: number) => void
  onDeleteRequest: (e: Event) => void
}

function CommandRow({
  event,
  index,
  selectionMode,
  selected,
  onEventClick,
  onToggleSelect,
  onDeleteRequest,
}: CommandRowProps) {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const { original, annotated } = getEventDownloads(event)
  const maxConf = useMemo(() => maxConfidence(event), [event])
  const eventTypeDisplay = tc(`eventTypes.${event.event_type}`, { defaultValue: event.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) })

  const handleRowClick = () => {
    if (selectionMode) {
      onToggleSelect(event.id)
    } else {
      onEventClick(index)
    }
  }

  return (
    <tr
      className={cn(
        'border-b border-border cursor-pointer transition-all duration-100',
        'hover:bg-accent/40 hover:shadow-[inset_2px_0_0_hsl(var(--primary))]',
        selected && 'bg-primary/10',
      )}
      onClick={handleRowClick}
      data-testid="command-row"
    >
      {/* Checkbox cell — only in selection mode */}
      {selectionMode && (
        <td className="px-3 py-2 w-8">
          <div
            className="flex items-center"
            onClick={(e) => { e.stopPropagation(); onToggleSelect(event.id) }}
          >
            <SelectCell selected={selected} />
          </div>
        </td>
      )}

      {/* Thumbnail */}
      <td className="px-3 py-2">
        <Thumb event={event} />
      </td>

      {/* Timestamp */}
      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground tabular-nums">
        {formatRelativeTime(event.occurred_at)}
      </td>

      {/* Camera */}
      <td className="px-3 py-2 max-w-[180px] truncate text-foreground">
        {event.camera_name}
      </td>

      {/* Detection chips */}
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {event.detected_objects.slice(0, 3).map((cls) => (
            <DetectionChip key={cls} cls={cls} type="object" />
          ))}
          {event.audio_detections.slice(0, 2).map((d) => (
            <DetectionChip key={d.class_name} cls={d.class_name} confidence={d.confidence} type="audio" />
          ))}
          {!event.detected_objects.length && !event.audio_detections.length && (
            <span className="text-muted-foreground/50">{eventTypeDisplay}</span>
          )}
        </div>
      </td>

      {/* Confidence bar */}
      <td className="px-3 py-2">
        <ConfBar pct={maxConf} />
      </td>

      {/* Actions menu */}
      <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
        {!selectionMode && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
              >
                <MoreVertical className="h-3.5 w-3.5" />
                <span className="sr-only">{t('card.eventOptions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEventClick(index)}>
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('card.open')}
              </DropdownMenuItem>
              {original && (
                <DropdownMenuItem
                  onClick={() => downloadFile(original.url, original.filename)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {t('detail.downloadOriginal')}
                </DropdownMenuItem>
              )}
              {annotated && (
                <DropdownMenuItem
                  onClick={() => downloadFile(annotated.url, annotated.filename)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {t('detail.downloadAnnotated')}
                </DropdownMenuItem>
              )}
              {(original || annotated) && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={() => onDeleteRequest(event)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('card.deleteEvent')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  )
}
